import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  Stack,
  TextField,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  createOrUpdateDeliveryDetails,
  getMoveService,
} from "../redux/action/move-service";

interface ChangeDeliveryModalProps {
  open: boolean;
  onClose: () => void;
  canonicalId: string;
}

const ChangeDeliveryModal: React.FC<ChangeDeliveryModalProps> = ({
  open,
  onClose,
  canonicalId,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dwellingType, setDwellingType] = useState<
    "home" | "apartment" | "storage" | ""
  >("");
  const [accessType, setAccessType] = useState<
    "elevator" | "stairs" | "not-applicable" | ""
  >("");
  const [floorNumber, setFloorNumber] = useState("");
  const [floorNumberError, setFloorNumberError] = useState("");
const [moveId,setMoveId] = useState(null)
  // Fetch delivery details on open
  useEffect(() => {
    if (open && canonicalId) {
      setIsLoading(true);
      setError(null);

      dispatch<any>(getMoveService(canonicalId))
        .then(unwrapResult)
        .then((move: any) => {
          setMoveId(move.id)
          const deliveryDetails = move.deliveryDetails || {};

          setDwellingType(deliveryDetails.dwelling_type || "");
          setFloorNumber(deliveryDetails.floor_number || "");
          if (deliveryDetails.isElevator === true) setAccessType("elevator");
          else if (deliveryDetails.isElevator === false)
            setAccessType("stairs");
          else setAccessType("not-applicable");

          setIsLoading(false);
        })
        .catch((err: any) => {
          setError("Failed to fetch delivery details");
          setIsLoading(false);
        });
    }
  }, [open, canonicalId, dispatch]);

  const handleFloorNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    const numberValue = parseInt(value);
    if (numberValue > 20) {
      setFloorNumberError("Floor number cannot be greater than 20");
      setFloorNumber("20");
    } else {
      setFloorNumberError("");
      setFloorNumber(value);
    }
  };

  const AccessOption = ({
    type,
    label,
  }: {
    type: "elevator" | "stairs" | "not-applicable";
    label: string;
  }) => (
    <Button
      fullWidth
      variant={accessType === type ? "contained" : "outlined"}
      onClick={() => {
        setAccessType(type);
        if (type === "not-applicable") setFloorNumber("0");
      }}
      sx={{
        height: 100,
        borderColor: theme.palette.primary.main,
        "&:hover": {
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      {label}
    </Button>
  );

  const handleSubmit = async () => {
    if (!dwellingType) {
      setError("Please select a dwelling type");
      return;
    }
    if (dwellingType !== "home" && !accessType) {
      setError("Please select access type");
      return;
    }
    if (
      (dwellingType === "apartment" || dwellingType === "storage") &&
      accessType !== "not-applicable" &&
      !floorNumber
    ) {
      setError("Please enter floor number");
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const deliveryData = {
        dwelling_type: dwellingType,
        floor_number: accessType === "not-applicable" ? "0" : floorNumber,
        isElevator:
          accessType === "elevator"
            ? true
            : accessType === "stairs"
            ? false
            : null,
        moveService_id:  moveId,
      };
      const res = await dispatch<any>(
        createOrUpdateDeliveryDetails(deliveryData)
      );
      unwrapResult(res);
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to update delivery details");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="change-delivery-modal"
    >
      <Box
        sx={{
          position: "absolute" as "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 420,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          outline: "none",
        }}
      >
        <Typography id="change-delivery-modal" variant="h6" mb={2}>
          Change Delivery Details
        </Typography>

        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 120,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box mb={2}>
              <Typography mb={1}>Dwelling Type</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={dwellingType === "home" ? "contained" : "outlined"}
                  onClick={() => {
                    setDwellingType("home");
                    setAccessType("");
                    setFloorNumber("");
                  }}
                  sx={{ flex: 1 }}
                >
                  Home
                </Button>
                <Button
                  variant={
                    dwellingType === "apartment" ? "contained" : "outlined"
                  }
                  onClick={() => setDwellingType("apartment")}
                  sx={{ flex: 1 }}
                >
                  Apartment
                </Button>
                <Button
                  variant={
                    dwellingType === "storage" ? "contained" : "outlined"
                  }
                  onClick={() => setDwellingType("storage")}
                  sx={{ flex: 1 }}
                >
                  Storage
                </Button>
              </Stack>
            </Box>

            {(dwellingType === "apartment" || dwellingType === "storage") && (
              <>
                <Box mb={2}>
                  <Typography mb={1}>Access Type</Typography>
                  <Stack direction="row" spacing={1}>
                    <AccessOption type="stairs" label="Stairs" />
                    <AccessOption type="elevator" label="Elevator" />
                    <AccessOption
                      type="not-applicable"
                      label="Not Applicable"
                    />
                  </Stack>
                </Box>

                {accessType && accessType !== "not-applicable" && (
                  <Box mb={2}>
                    <Typography mb={1}>Floor Number</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={floorNumber}
                      onChange={handleFloorNumberChange}
                      inputProps={{ min: 0, max: 20 }}
                      error={Boolean(floorNumberError)}
                      helperText={floorNumberError}
                    />
                  </Box>
                )}
              </>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit}>
                Save
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default ChangeDeliveryModal;
